/**
 * Gedeelde UI-bouwstenen voor de Marketing-module.
 */
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useUserRole } from '../../hooks/useUserRole';

export const STATUS_LABELS: Record<string, string> = {
  draft: 'Concept',
  pending_approval: 'Wacht op goedkeuring',
  approved: 'Goedgekeurd',
  scheduled: 'Ingepland',
  sending: 'Versturen…',
  sent: 'Verstuurd',
  cancelled: 'Geannuleerd',
  active: 'Actief',
  paused: 'Gepauzeerd',
  archived: 'Gearchiveerd',
  queued: 'In wachtrij',
  delivered: 'Bezorgd',
  bounced: 'Gebounced',
  complained: 'Spamklacht',
  failed: 'Mislukt',
  suppressed: 'Onderdrukt',
  completed: 'Afgerond',
  exited_order: 'Gestopt — bestelling',
  exited_replied: 'Gestopt — reactie',
  exited_unsubscribed: 'Gestopt — uitgeschreven',
  exited_manual: 'Gestopt — handmatig',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600',
  pending_approval: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  scheduled: 'bg-blue-100 text-blue-800',
  sending: 'bg-indigo-100 text-indigo-800',
  sent: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-slate-100 text-slate-500',
  active: 'bg-emerald-100 text-emerald-800',
  paused: 'bg-amber-100 text-amber-800',
  archived: 'bg-slate-100 text-slate-500',
  queued: 'bg-slate-100 text-slate-600',
  delivered: 'bg-emerald-100 text-emerald-800',
  bounced: 'bg-red-100 text-red-700',
  complained: 'bg-red-100 text-red-700',
  failed: 'bg-red-100 text-red-700',
  suppressed: 'bg-amber-100 text-amber-800',
  completed: 'bg-emerald-100 text-emerald-800',
  exited_order: 'bg-emerald-100 text-emerald-800',
  exited_replied: 'bg-blue-100 text-blue-800',
  exited_unsubscribed: 'bg-slate-100 text-slate-500',
  exited_manual: 'bg-slate-100 text-slate-500',
};

export const SUPPRESSION_REASON_LABELS: Record<string, string> = {
  unsubscribed: 'Uitgeschreven',
  replied_auto: 'Heeft gereageerd (automatisch)',
  replied_manual: 'Heeft gereageerd (handmatig)',
  complaint: 'Spamklacht',
  manual: 'Handmatig',
  bounce_hard: 'Hard bounce',
  not_subscribed: 'Niet ingeschreven',
  suppressed_global: 'Globaal onderdrukt',
  suppressed_campaign: 'Onderdrukt voor campagne',
  suppressed_journey: 'Onderdrukt voor journey',
  blacklisted: 'Blacklist',
  open_complaint: 'Open klacht',
  frequency_cap_week: 'Weeklimiet bereikt',
  frequency_cap_month: 'Maandlimiet bereikt',
};

export function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
        STATUS_COLORS[status] || 'bg-slate-100 text-slate-600'
      }`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('nl-NL', { dateStyle: 'medium', timeStyle: 'short' });
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('nl-NL', { dateStyle: 'medium' });
}

export function SectionCard({ title, children, actions }: { title?: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      {(title || actions) && (
        <div className="flex items-center justify-between mb-4">
          {title && <h3 className="font-semibold text-gray-900">{title}</h3>}
          {actions}
        </div>
      )}
      {children}
    </div>
  );
}

export function StatTile({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="text-2xl font-semibold text-gray-900 mt-1">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

const NAV_ITEMS = [
  { to: '/marketing', label: 'Dashboard', end: true },
  { to: '/marketing/campagnes', label: 'Campagnes' },
  { to: '/marketing/journeys', label: 'Journeys' },
  { to: '/marketing/segmenten', label: 'Segmenten' },
  { to: '/marketing/contacten', label: 'Contacten' },
  { to: '/marketing/social', label: 'Social' },
  { to: '/marketing/gbp', label: 'Google Bedrijfsprofiel' },
  { to: '/marketing/beeldbibliotheek', label: 'Beeldbibliotheek' },
  { to: '/marketing/instellingen', label: 'Instellingen' },
];

/** Tabs die een medewerker mag zien; de rest van Marketing is admin (zie App.tsx-guards). */
const MEDEWERKER_TABS = new Set(['/marketing/social']);

export function MarketingNav() {
  const { role } = useUserRole();
  const items = role === 'admin' ? NAV_ITEMS : NAV_ITEMS.filter((i) => MEDEWERKER_TABS.has(i.to));
  return (
    <div className="flex shrink-0 items-center gap-1 overflow-x-auto border-b border-gray-200 px-6 bg-white">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            `whitespace-nowrap px-3 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
              isActive
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`
          }
        >
          {item.label}
        </NavLink>
      ))}
    </div>
  );
}

export function MarketingPageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="flex items-start justify-between px-6 pt-6 pb-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}
